/**
 * What repeats in a group: rent, the internet bill, the ones nobody wants to
 * file twelve times a year.
 *
 * A rule is a *template* — an amount, a split, who pays — plus the date it
 * next falls due, and this screen never writes an expense. The scheduled
 * Worker does that, nightly (`worker/src/index.ts`, through
 * `server/recurring.ts`'s `materializeDueRecurring`), which is the one thing
 * this screen has to be honest about: a date that has come and gone with no
 * expense against it yet is normal, not broken, and every sentence here says
 * "will be added" rather than pretending it already has been.
 *
 * **The sheet is the expense sheet.** `RuleSheet` below holds a
 * `RecurringSheet`, which is an `ExpenseSheet` plus a start date, an optional
 * end and a note — so the amount pad, the payer editor, the split editor and
 * the preview are literally the ones `src/screens/AddExpense.tsx` uses, and a
 * rule is refused for the same reasons in the same words an expense is.
 * `src/lib/recurring.ts` is where everything that decides anything lives.
 *
 * **Correcting a rule cannot move it to another date.** `startOn` is
 * create-only — `server/recurring.ts` says why — so the sheet shows it back
 * as words when it is editing rather than as a field that would silently do
 * nothing. Nothing already materialised is touched by an edit either: those
 * are ordinary expenses by now, correctable on the group's own screen like
 * any other.
 *
 * **Three writes and the reason each waits.** None of them is optimistic
 * (`src/queries.ts` says why: a rule moves no balance), so the sheet stays
 * open and disabled until the answer lands, Skip and Cancel keep their rows
 * where they are, and a refusal — `occurs_on_mismatch`, most plausibly, from
 * the Worker having moved the date while this list sat on somebody's
 * screen — is shown against the rule it belongs to rather than at the top of
 * the screen.
 */

import { useMemo, useState, type FormEvent } from 'react'
import type { Member, RecurringRule } from '../api'
import { AmountPad } from '../components/AmountPad'
import { CategoryPicker } from '../components/CategoryPicker'
import { PayerEditor } from '../components/PayerEditor'
import { SplitEditor } from '../components/SplitEditor'
import { categoryFor } from '../lib/categories'
import { formatDated, formatMoney } from '../lib/format'
import {
  describeEnd,
  describeNext,
  describeParties,
  describeOccurrence,
  describeSkipped,
  describeSchedule,
  firstProblem,
  MAX_DESCRIPTION,
  MAX_NOTE,
  MAX_START_DAY,
  newRecurringSheet,
  recurringSheetFrom,
  resolveRecurringSheet,
  ruleState,
  startOnOf,
  type RecurringSheet,
} from '../lib/recurring'
import { staggerStyle } from '../motion-react'
import {
  useCancelRecurring,
  useCreateRecurring,
  useGroup,
  useRecurring,
  useRecurringRule,
  useSkipRecurring,
  useUpdateRecurring,
} from '../queries'
import type { Route } from '../routes'

const today = () => new Date().toISOString().slice(0, 10)

/** The sheet, when it is open: making a new rule, or correcting one. */
type Panel = { kind: 'none' } | { kind: 'new' } | { kind: 'edit'; rule: RecurringRule }

export function Recurring({
  groupId,
  myId,
  navigate,
}: {
  groupId: string
  myId: string
  navigate: (to: Route) => void
}) {
  const [panel, setPanel] = useState<Panel>({ kind: 'none' })
  /** Which rule's history is open. By id, so it survives the list refetching. */
  const [openRule, setOpenRule] = useState<string | null>(null)
  /** Which Cancel has been pressed once and is waiting to be pressed again. */
  const [asking, setAsking] = useState<string | null>(null)
  /**
   * The date each rule has just had skipped, by rule id.
   *
   * A skip does not move `nextOccursOn` — the Worker does that when the date
   * comes and finds the skip already recorded — so nothing in the refetched
   * list says the button worked. Holding the answer's own date here, and
   * showing it only while it is still the rule's upcoming one, says it
   * without claiming anything the server has not already agreed to.
   */
  const [skipped, setSkipped] = useState<Record<string, string>>({})

  const group = useGroup(groupId)
  const rules = useRecurring(groupId)
  const skip = useSkipRecurring(groupId)
  const cancel = useCancelRecurring(groupId)

  const currency = group.data?.group.currency ?? 'USD'
  const groupName = group.data?.group.name ?? 'Group'
  const members = group.data?.members ?? []
  const current = members.filter((member) => member.leftAt === null)
  const nameOf = new Map(members.map((member) => [member.userId, member.displayName]))
  const archived = group.data?.group.archivedAt != null
  const now = today()

  /** Which rule a failed Skip or Cancel belongs to, so the row says so and not the screen. */
  const failedOn = (recurringId: string) => {
    if (skip.isError && skip.variables?.recurringId === recurringId) return skip.error.message
    if (cancel.isError && cancel.variables === recurringId) return cancel.error.message
    return null
  }

  return (
    <>
      <p className="crumbs">
        <button
          type="button"
          className="button--quiet"
          onClick={() => navigate({ name: 'group', groupId })}
        >
          ← {groupName}
        </button>
      </p>

      <section className="card">
        <div className="card__head">
          <h2>What repeats</h2>
          <button
            type="button"
            disabled={archived || current.length === 0}
            onClick={() =>
              setPanel(panel.kind === 'new' ? { kind: 'none' } : { kind: 'new' })
            }
          >
            {panel.kind === 'new' ? 'Close' : 'New repeating expense'}
          </button>
        </div>

        <p className="muted note">
          Rent, the internet, the ones that happen on the same date every month. Each is added to
          this group as an ordinary expense on its date — by a nightly run, not by this screen —
          and can be corrected or removed there like any other afterwards.
        </p>
        {archived && (
          <p className="badge">
            Archived — it takes no new rules, and an occurrence that falls due is recorded as
            skipped rather than added.
          </p>
        )}

        {panel.kind !== 'none' && group.data && (
          <RuleSheet
            // Re-mounted per rule, so opening a different one starts the sheet
            // from that rule's own inputs rather than leaving the last one's
            // typing in the boxes.
            key={panel.kind === 'edit' ? panel.rule.id : 'new'}
            groupId={groupId}
            currency={currency}
            myId={myId}
            members={members}
            editing={panel.kind === 'edit' ? panel.rule : undefined}
            onDone={() => setPanel({ kind: 'none' })}
          />
        )}

        {rules.isPending && <p className="muted">Fetching what repeats…</p>}
        {rules.isError && <p className="bad">{rules.error.message}</p>}
        {rules.data?.recurring.length === 0 && (
          <p className="muted">Nothing repeats in this group yet.</p>
        )}

        <ul className="rows">
          {rules.data?.recurring.map((rule, index) => {
            const category = categoryFor(rule.category)
            const state = ruleState(rule, now)
            const payers = rule.paid.map((allocation) =>
              allocation.userId === myId
                ? 'You'
                : (nameOf.get(allocation.userId) ?? allocation.userId),
            )
            // "You pay", "Ada pays", "Ada and Ben pay".
            const plural = rule.paid.length > 1 || rule.paid[0]?.userId === myId
            const end = describeEnd(rule)
            const failure = failedOn(rule.id)
            /** Still the upcoming date, so the skip this browser made still stands. */
            const justSkipped = skipped[rule.id] === rule.nextOccursOn

            return (
              <li key={rule.id} className="rule" style={staggerStyle(index)}>
                <span className="rule__what">
                  {rule.description}
                  {category && (
                    <span className="pill">
                      <span aria-hidden="true">{category.glyph}</span>
                      {category.label}
                    </span>
                  )}
                  {rule.kind === 'loan' && <span className="pill">loan</span>}
                  {state === 'due' && !justSkipped && <span className="pill pill--pending">Due</span>}
                  {justSkipped && <span className="pill pill--pending">Skipped</span>}
                  {state === 'finished' && <span className="pill">Finished</span>}
                </span>
                <span className="rule__amount">{formatMoney(rule.amountCents, currency)}</span>

                <span className="rule__when">
                  {describeSchedule(rule)}
                  {end && ` ${end}`}.{' '}
                  {describeParties(payers, rule.split.participants.length, plural)}.{' '}
                  {describeNext(rule, now)}
                </span>

                <span className="rule__actions">
                  {/*
                    Only the upcoming occurrence can be skipped — a rule has
                    exactly one date that has not happened yet — so a finished
                    series has nothing to skip, and an archived group's
                    occurrences are already being recorded as skips by the
                    Worker itself.
                  */}
                  <button
                    type="button"
                    className="button--quiet"
                    disabled={state === 'finished' || justSkipped || skip.isPending}
                    onClick={() =>
                      skip.mutate(
                        { recurringId: rule.id, occursOn: rule.nextOccursOn },
                        {
                          onSuccess: (answer) =>
                            setSkipped((previous) => ({ ...previous, [rule.id]: answer.skipped })),
                        },
                      )
                    }
                  >
                    Skip {formatDated(rule.nextOccursOn)}
                    <span className="visually-hidden"> for {rule.description}</span>
                  </button>
                  <button
                    type="button"
                    className="button--quiet"
                    disabled={archived}
                    onClick={() => setPanel({ kind: 'edit', rule })}
                  >
                    Edit
                    <span className="visually-hidden"> {rule.description}</span>
                  </button>
                  <button
                    type="button"
                    className="button--quiet"
                    aria-expanded={openRule === rule.id}
                    onClick={() => setOpenRule(openRule === rule.id ? null : rule.id)}
                  >
                    History
                    <span className="visually-hidden"> of {rule.description}</span>
                  </button>
                  {/*
                    Asks twice. Cancelling stops a series somebody set up to
                    stop thinking about, and the button is beside two that are
                    harmless — `src/screens/Admin.tsx` asks the same way for
                    the same reason.
                  */}
                  <button
                    type="button"
                    className="button--danger"
                    disabled={cancel.isPending}
                    onClick={() => {
                      if (asking !== rule.id) {
                        setAsking(rule.id)
                        return
                      }
                      setAsking(null)
                      cancel.mutate(rule.id)
                    }}
                  >
                    {asking === rule.id ? 'Cancel it — press again' : 'Cancel'}
                    <span className="visually-hidden"> {rule.description}</span>
                  </button>
                </span>

                {justSkipped && <p className="muted note rule__wide">{describeSkipped(rule.nextOccursOn)}</p>}
                {failure && <p className="bad note rule__wide">{failure}</p>}

                {openRule === rule.id && (
                  <RuleHistory
                    groupId={groupId}
                    rule={rule}
                    nameOf={nameOf}
                    onOpenExpense={() => navigate({ name: 'group', groupId })}
                  />
                )}
              </li>
            )
          })}
        </ul>
      </section>
    </>
  )
}

/**
 * What one rule has already done, and who is in it.
 *
 * A second request per rule, made only when a row is opened — the same
 * bargain the settlement plan makes. The list above says everything somebody
 * scanning the screen needs; this is for the one rule they stopped at.
 */
function RuleHistory({
  groupId,
  rule,
  nameOf,
  onOpenExpense,
}: {
  groupId: string
  rule: RecurringRule
  nameOf: Map<string, string>
  onOpenExpense: () => void
}) {
  const detail = useRecurringRule(groupId, rule.id, true)
  const occurrences = detail.data?.occurrences ?? []

  return (
    <div className="rule__history">
      <p className="muted note">
        Split between{' '}
        {rule.split.participants
          .map((participant) => nameOf.get(participant.userId) ?? participant.userId)
          .join(', ')}
        .{rule.note && ` ${rule.note}`}
      </p>

      {detail.isPending && <p className="muted note">Looking up what it has done…</p>}
      {detail.isError && <p className="bad note">{detail.error.message}</p>}
      {detail.data && occurrences.length === 0 && (
        <p className="muted note">Nothing has fallen due yet.</p>
      )}

      {occurrences.length > 0 && (
        <ul className="rule__occurrences">
          {occurrences.map((occurrence) => (
            <li key={occurrence.occursOn}>
              {describeOccurrence(occurrence)}
              {/*
                An occurrence that made an expense made an ordinary one, and
                the group's own screen is where every expense is read and
                corrected. There is no separate place for these.
              */}
              {occurrence.transactionId !== null && (
                <>
                  {' — '}
                  <button type="button" className="button--quiet" onClick={onOpenExpense}>
                    see it on the group
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The sheet: the expense sheet, plus the two dates and the note that make it
 * a schedule.
 *
 * It waits for its answer, unlike filing an expense. Filing is optimistic and
 * has nothing left to show once the mutation is fired; this is not, so
 * closing early would leave somebody looking at a list that has not changed
 * with no way to tell whether anything happened.
 */
function RuleSheet({
  groupId,
  currency,
  myId,
  members,
  editing,
  onDone,
}: {
  groupId: string
  currency: string
  myId: string
  /** Every member the group has ever had, in the group's own order. */
  members: readonly Member[]
  editing?: RecurringRule
  onDone: () => void
}) {
  const current = useMemo(() => members.filter((member) => member.leftAt === null), [members])

  /**
   * Anybody the rule already names stays on the sheet even if they have since
   * left the group — the same rule the expense sheet follows, for the same
   * reason: a rule they are part of has to remain correctable.
   */
  const onSheet = useMemo(() => {
    if (!editing) return current
    const named = new Set([
      ...editing.paid.map((allocation) => allocation.userId),
      ...editing.split.participants.map((participant) => participant.userId),
    ])
    return members.filter((member) => member.leftAt === null || named.has(member.userId))
  }, [members, current, editing])

  const [sheet, setSheet] = useState<RecurringSheet>(() =>
    editing
      ? recurringSheetFrom(editing)
      : newRecurringSheet({
          memberIds: current.map((member) => member.userId),
          myId,
          today: today(),
        }),
  )

  const create = useCreateRecurring(groupId)
  const update = useUpdateRecurring(groupId)
  const write = editing ? update : create

  const memberOrder = onSheet.map((member) => member.userId)
  const { draft, preview, problems } = resolveRecurringSheet(sheet, memberOrder)
  const saving = write.isPending

  function submit(event: FormEvent) {
    event.preventDefault()
    if (draft === null || saving) return

    if (editing) {
      update.mutate({ recurringId: editing.id, draft }, { onSuccess: onDone })
      return
    }
    create.mutate(draft, { onSuccess: onDone })
  }

  /** The template's own fields — the expense sheet inside this one. */
  const setTemplate = <K extends keyof RecurringSheet['template']>(
    key: K,
    value: RecurringSheet['template'][K],
  ) => setSheet((previous) => ({ ...previous, template: { ...previous.template, [key]: value } }))

  return (
    <form className="form sheet" onSubmit={submit}>
      <div className="form__row">
        <span id="rule-amount-label" className="sheet__label">
          How much, every month?
        </span>
        <AmountPad
          amountCents={sheet.template.amountCents}
          currency={currency}
          labelledBy="rule-amount-label"
          onChange={(cents) => setTemplate('amountCents', cents)}
        />
      </div>

      <div className="form__row">
        <label htmlFor="rule-description">What is it?</label>
        <input
          id="rule-description"
          value={sheet.template.description}
          maxLength={MAX_DESCRIPTION}
          required
          placeholder="Rent"
          onChange={(event) => setTemplate('description', event.target.value)}
        />
      </div>

      {/* The same one-tap picker the expense sheet uses; a rule is a template
          for an expense, so it should not ask the question differently. */}
      <div className="form__row">
        <span id="rule-category-label" className="sheet__label">
          What for?
        </span>
        <CategoryPicker
          value={sheet.template.category}
          labelledBy="rule-category-label"
          onChange={(category) => setTemplate('category', category)}
        />
      </div>

      <div className="form__row form__row--narrow">
        <label htmlFor="rule-kind">Which kind?</label>
        {/*
          Never a settlement. A payment between two people happens on the day
          it happens, and `server/recurring.ts` refuses one on a schedule —
          so it is not in this list rather than being refused after the fact.
        */}
        <select
          id="rule-kind"
          value={sheet.kind}
          onChange={(event) =>
            setSheet((previous) => ({
              ...previous,
              kind: event.target.value === 'loan' ? 'loan' : 'expense',
            }))
          }
        >
          <option value="expense">An expense</option>
          <option value="loan">A loan</option>
        </select>
      </div>

      <div className="form__pair">
        <div className="form__row">
          <label htmlFor="rule-start">Starting when?</label>
          {/*
            Create-only. `PATCH .../recurring/:id` does not read `startOn`, so
            an editable box here would be a field that quietly did nothing;
            what a person wants when they need another date is a new series,
            and cancelling this one is two buttons away.
          */}
          {editing ? (
            <p className="sheet__reading" id="rule-start">
              {formatDated(startOnOf(sheet))} — and the same day every month after.
              <br />
              <span className="muted">
                A series cannot be moved to another date. Cancel it and start another.
              </span>
            </p>
          ) : (
            <input
              id="rule-start"
              type="date"
              value={startOnOf(sheet)}
              onChange={(event) => setTemplate('occurredOn', event.target.value)}
            />
          )}
        </div>

        <div className="form__row">
          <label htmlFor="rule-end">Until when?</label>
          <input
            id="rule-end"
            type="date"
            value={sheet.endOn}
            onChange={(event) =>
              setSheet((previous) => ({ ...previous, endOn: event.target.value }))
            }
          />
          <span className="muted note">Leave it empty to run until you cancel it.</span>
        </div>
      </div>

      {!editing && (
        <p className="muted note">
          Any date from the 1st to the {MAX_START_DAY}th, so every month has that date to land on.
        </p>
      )}

      <PayerEditor
        payers={sheet.template.payers}
        amountCents={sheet.template.amountCents}
        currency={currency}
        myId={myId}
        members={onSheet}
        tense="present"
        onChange={(next) => setTemplate('payers', next)}
      />

      <SplitEditor
        split={sheet.template.split}
        amountCents={sheet.template.amountCents}
        currency={currency}
        myId={myId}
        members={onSheet}
        onChange={(next) => setTemplate('split', next)}
      />

      <div className="form__row">
        <label htmlFor="rule-note">Anything to note?</label>
        <input
          id="rule-note"
          value={sheet.note}
          maxLength={MAX_NOTE}
          placeholder="Optional"
          onChange={(event) => setSheet((previous) => ({ ...previous, note: event.target.value }))}
        />
      </div>

      {write.isError && <p className="bad">{write.error.message}</p>}

      <p className="actions">
        <button type="submit" disabled={draft === null || saving}>
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Set it up'}
        </button>
        <button type="button" className="button--quiet" disabled={saving} onClick={onDone}>
          Cancel
        </button>
        <span className="muted note">
          {draft !== null
            ? `${formatMoney(preview.totalCents, currency)} a month between ${
                preview.allocations.length
              } ${preview.allocations.length === 1 ? 'person' : 'people'}.`
            : (firstProblem(problems) ?? '')}
        </span>
      </p>
    </form>
  )
}
